import { Pin } from 'lucide-react';
import { LiksLucideIcon } from '../../components/liks/LiksLucideIcon/LiksLucideIcon';
import type { KnowledgeBaseWithIdentity } from '../../types/knowledge';
import { normalizeLucideIconName } from '../../utils/lucideIconCatalog';
import './KnowledgeDatabaseCard.css';

type KnowledgeDatabaseCardProps = {
  database: KnowledgeBaseWithIdentity;
  isPinned: boolean;
  lastModifiedLabel: string;
  translate: (key: string, params?: Record<string, string>) => string;
  onTogglePin: (databaseId: string) => void;
  onOpen: (databaseId: string) => void;
};

export function KnowledgeDatabaseCard({
  database,
  isPinned,
  lastModifiedLabel,
  translate,
  onTogglePin,
  onOpen,
}: KnowledgeDatabaseCardProps) {
  const description = database.description?.trim() ?? '';
  const databaseId = database.id;

  return (
    <li>
      <div className="knowledge-page__card">
        <button
          type="button"
          className={[
            'knowledge-page__card-pin',
            isPinned && 'knowledge-page__card-pin--active',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-pressed={isPinned}
          aria-label={
            isPinned
              ? translate('knowledge.unpinDatabase', {
                  title: database.name ?? '',
                })
              : translate('knowledge.pinDatabase', {
                  title: database.name ?? '',
                })
          }
          onClick={() => onTogglePin(databaseId)}
        >
          <Pin
            className="knowledge-page__card-pin-icon"
            strokeWidth={2}
            fill={isPinned ? 'currentColor' : 'none'}
            aria-hidden
          />
        </button>
        <button
          type="button"
          className="knowledge-page__card-main"
          onClick={() => onOpen(databaseId)}
        >
          <span className="knowledge-page__card-heading">
            <span className="knowledge-page__card-icon" aria-hidden>
              <LiksLucideIcon
                name={normalizeLucideIconName(database.icon)}
                strokeWidth={2}
              />
            </span>
            <span className="knowledge-page__card-title">{database.name}</span>
            {database.role ? (
              <span className="knowledge-page__card-badge">
                {translate(`knowledge.members.roles.${database.role}`)}
              </span>
            ) : null}
          </span>
          <span
            className={[
              'knowledge-page__card-description',
              description === ''
                ? 'knowledge-page__card-description--empty'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {description !== ''
              ? description
              : translate('knowledge.noDescription')}
          </span>
          <dl className="knowledge-page__card-stats">
            <div className="knowledge-page__card-stat">
              <dt>{translate('knowledge.stats.documents')}</dt>
              <dd>{database.documentCount ?? 0}</dd>
            </div>
            <div className="knowledge-page__card-stat">
              <dt>{translate('knowledge.stats.crawls')}</dt>
              <dd>{database.crawlsCount ?? 0}</dd>
            </div>
            <div className="knowledge-page__card-stat">
              <dt>{translate('knowledge.stats.members')}</dt>
              <dd>{database.memberCount ?? 0}</dd>
            </div>
            <div className="knowledge-page__card-stat">
              <dt>{translate('knowledge.stats.lastModified')}</dt>
              <dd>{lastModifiedLabel}</dd>
            </div>
          </dl>
        </button>
      </div>
    </li>
  );
}
